import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import StrategyNameButton from "@/components/strategy/StrategyNameButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import ScannerLink from "@/components/ScannerLink";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { priceLabel } from "@/lib/priceLabel";
import ExpiryBadge from "@/components/paper/ExpiryBadge";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from "recharts";
import { ArrowRight, ShieldAlert, Activity, Sparkles, Pause, Play, RefreshCw, User, Bot } from "lucide-react";

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const AUTO_REFRESH_MS = 5000;
// RU-COCKPITUI CU-5: a tile group whose data set has not refreshed successfully for longer than this shows a banner.
export const STALE_AFTER_S = 15;
// CU-13: each cockpit request is given up (aborted, counted as a failure, slot freed) after this long.
export const REQUEST_TIMEOUT_MS = 15000;
// CU-7: option-chain underlyings whose scan spot is a cash index (any other symbol is a stock's cash price).
const INDEX_SYMBOLS = new Set(["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX", "BANKEX"]);
// CU-10: MCX underlyings (same list as backend market_data._MCX_SYMBOLS): their scan spot is the near future.
const MCX_SYMBOLS = new Set(["GOLD", "SILVER", "CRUDEOIL", "NATURALGAS", "COPPER", "ZINC"]);

/** CU-7 / CU-10: what the trap scan's spot is for this underlying. */
export function spotKindOf(symbol) {
  const sym = String(symbol || "").toUpperCase();
  if (INDEX_SYMBOLS.has(sym)) return "cash index";
  if (MCX_SYMBOLS.has(sym)) return "future";
  return "cash";
}
const SET_NAMES = { summary: "summary", market: "chart", trap: "trap scan" };

/** CU-7: chain source label per lib/priceLabel (live → LIVE, mock → SIMULATED, anything else → DELAYED (source)). */
export function chainSourceLabel(scan) {
  if (!scan) return null;
  const src = String(scan.source ?? "unknown");
  const lab = priceLabel({ ltp: scan.spot ?? 0, source: src, is_live: src === "live" });
  return lab === "DELAYED" ? `DELAYED (${src})` : lab;
}

/** CU-5: "data not refreshed for Ns" banner for one tile group; nothing while fresh. */
function StaleBanner({ testid, ageS }) {
  if (ageS == null || ageS <= STALE_AFTER_S) return null;
  return (
    <div
      data-testid={testid}
      role="status"
      aria-live="polite"
      className="text-[11px] font-mono-data txt-warn border border-amber-500/30 bg-amber-500/5 px-3 py-1.5"
    >
      data not refreshed for {Math.floor(ageS)}s
    </div>
  );
}

// ── Trade Style Radar ─────────────────────────────────────────────────────────

function RadarLabel({ label }) {
  const SHORT = {
    speed: "Speed", accuracy: "Accuracy", consistency: "Consistency",
    aggression: "Aggression", diversification: "Diversif.",
  };
  return (
    <text style={{ fill: "var(--text-muted)", fontSize: 9, fontFamily: "JetBrains Mono" }}>
      {SHORT[label] || label}
    </text>
  );
}

function TradeStyleCard({ profile }) {
  if (!profile) {
    return (
      <div className="panel p-5 flex flex-col">
        <div className="overline mb-1">Trade Style</div>
        <div className="text-[var(--text-muted)] text-sm font-section mt-3">Loading…</div>
      </div>
    );
  }
  if (!profile.radar) {
    return (
      <div className="panel p-5 flex flex-col">
        <div className="overline mb-1">Trade Style</div>
        <div className="text-[var(--text-muted)] text-sm font-section mt-3">
          {profile.style_label} · {profile.trade_count} trade{profile.trade_count !== 1 ? "s" : ""} needed
        </div>
      </div>
    );
  }

  const radarData = Object.entries(profile.radar).map(([key, val]) => ({
    axis: key,
    value: Math.round(val * 100),
  }));

  const dim = profile.dimensions || {};
  const peakH = dim.peak_hour_ist != null
    ? `${dim.peak_hour_ist}:00–${dim.peak_hour_ist + 1}:00 IST`
    : "—";

  return (
    <div className="panel p-5 flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <div className="overline">Trade Style</div>
        <User className="w-3.5 h-3.5 text-[var(--text-muted)]" />
      </div>
      <div className="text-[15px] font-section text-[var(--text-primary)] mb-3 leading-tight">
        {profile.style_label}
      </div>

      {/* Radar */}
      <div className="h-40 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <PolarGrid stroke="rgba(255,255,255,0.07)" />
            <PolarAngleAxis dataKey="axis" tick={<RadarLabel />} />
            <Radar
              dataKey="value"
              stroke="var(--accent)"
              fill="var(--accent)"
              fillOpacity={0.18}
              strokeWidth={1.5}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Dimension pills */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] mt-2">
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)] font-section">Win rate</span>
          <span className="font-mono-data text-[var(--text-secondary)]">{dim.win_rate_pct ?? "—"}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)] font-section">Avg hold</span>
          <span className="font-mono-data text-[var(--text-secondary)]">
            {dim.avg_hold_hours != null ? `${dim.avg_hold_hours}h` : "—"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)] font-section">Bias</span>
          <span className="font-mono-data text-[var(--text-secondary)]">
            {dim.bullish_bias_pct != null ? `${dim.bullish_bias_pct}% Bull` : "—"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-muted)] font-section">Peak hour</span>
          <span className="font-mono-data text-[var(--text-secondary)]">{peakH}</span>
        </div>
        <div className="flex justify-between col-span-2">
          <span className="text-[var(--text-muted)] font-section">Top scanner</span>
          <span className="font-mono-data text-[var(--text-secondary)] capitalize">
            {/* RU-SCANPERF SP-9: a known scanner opens its performance page */}
            {dim.top_scanner
              ? <ScannerLink scanner={dim.top_scanner} testid="dash-top-scanner-link">{dim.top_scanner}</ScannerLink>
              : "—"}
          </span>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-[var(--border)] text-[10px] text-[var(--text-muted)] font-section">
        {profile.trade_count} trades · {profile.days}d window
      </div>
    </div>
  );
}

// ── Scanner Performance Strip ─────────────────────────────────────────────────

export function ScannerStrip({ scanners }) {
  if (!scanners?.length) return null;

  const top = [...scanners].sort((a, b) => (b.sharpe ?? 0) - (a.sharpe ?? 0)).slice(0, 5);
  const maxSharpe = Math.max(...top.map((s) => Math.abs(s.sharpe ?? 0)), 0.01);

  const LABELS = {
    trap: "Trap", momentum: "Mom", commodity_gap: "Gap", orb: "ORB",
    vwap: "VWAP", mean_reversion: "MR", breakout: "BRK", fvg_ob: "FVG",
    liquidity_sweep: "Swp",
  };

  return (
    <div className="panel p-5">
      <div className="overline mb-3">Scanner Performance · 30d Sharpe</div>
      <div className="space-y-2.5">
        {top.map((s) => {
          const wr = s.win_rate_pct ?? 0;
          const sharpe = s.sharpe ?? 0;
          const barW = Math.max(2, Math.abs(sharpe) / maxSharpe * 100);
          const color = wr >= 60 ? "var(--profit)" : wr >= 45 ? "var(--warn)" : "var(--loss)";
          return (
            <div key={s.scanner} className="flex items-center gap-3">
              <span className="text-[10px] font-section text-[var(--text-muted)] w-10 shrink-0">
                <ScannerLink scanner={s.scanner} testid={`dash-scanner-link-${s.scanner}`}>
                  {LABELS[s.scanner] || s.scanner}
                </ScannerLink>
              </span>
              <div className="flex-1 h-1.5 bg-[var(--bg-surface-2)] relative">
                <div
                  className="absolute left-0 top-0 h-full transition-all duration-500"
                  style={{ width: `${barW}%`, background: color }}
                />
              </div>
              <span className="text-[10px] font-mono-data w-8 text-right" style={{ color }}>
                {wr.toFixed(0)}%
              </span>
              <span className="text-[10px] font-mono-data text-[var(--text-muted)] w-12 text-right">
                {sharpe > 0 ? "+" : ""}{sharpe.toFixed(2)}
              </span>
              <span className="text-[9px] font-section text-[var(--text-muted)] w-8 text-right">
                {s.trade_count}t
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Today's Equity Curve ──────────────────────────────────────────────────────

function buildEquityCurve(trades) {
  if (!trades?.length) return [];
  const sorted = [...trades].sort((a, b) =>
    (a.entry_at || "").localeCompare(b.entry_at || "")
  );
  let cumPnl = 0;
  return sorted.map((t) => {
    cumPnl += t.pnl || 0;
    const time = t.entry_at ? t.entry_at.slice(11, 16) : "";
    return { time, pnl: Math.round(cumPnl) };
  });
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [candles, setCandles] = useState([]);
  const [trapPreview, setTrapPreview] = useState(null);
  const [mode, setMode] = useState("combined");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // CU-5: last successful refresh per data set, and the first attempt (per symbol for chart/trap) as fallback.
  const [lastOk, setLastOk] = useState({ summary: null, market: null, trap: null });
  const [since, setSince] = useState(() => {
    const t = Date.now();
    return { summary: t, market: t, trap: t };
  });
  const [summaryAt, setSummaryAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [symbols, setSymbols] = useState([]);
  const [chartSymbol, setChartSymbol] = useState("NIFTY");
  const [profile, setProfile] = useState(null);
  const [scanners, setScanners] = useState([]);
  const [todayTrades, setTodayTrades] = useState([]);
  const [autopilotActivity, setAutopilotActivity] = useState([]);

  useEffect(() => {
    api.get("/market/symbols").then((r) => setSymbols(r.data?.symbols || [])).catch(() => {});
    // Trade style profile — load once, not on every refresh
    api.get("/quant/trade-profile").then((r) => setProfile(r.data)).catch(() => {});
    // Scanner performance
    api.get("/quant/scanner-performance?days=30").then((r) => setScanners(r.data?.ranked || [])).catch(() => {});
    // Autopilot activity log
    api.get("/orders/live/autopilot/activity").then((r) => setAutopilotActivity(r.data?.activity || [])).catch(() => {});
    // Today's paper trades for equity curve
    const today = new Date().toISOString().slice(0, 10);
    api.get("/paper/trades", { params: { limit: 200 } })
      .then((r) => {
        const trades = (r.data?.items || r.data || []).filter(
          (t) => t.pnl != null && t.entry_at?.startsWith(today)
        );
        setTodayTrades(trades);
      })
      .catch(() => {});
  }, []);

  // CU-4: request sequence per data set (an older response never overwrites a newer one) and the symbol on screen.
  const seqRef = useRef({ summary: 0, market: 0, trap: 0 });
  const appliedRef = useRef({ summary: 0, market: 0, trap: 0 });
  const symbolRef = useRef(chartSymbol);
  // CU-11: the request of each data set still in flight ({ tag, seq, cancel }); tag = "summary" or the symbol.
  const inflightRef = useRef({ summary: null, market: null, trap: null });

  // CU-14: give up the in-flight request of a data set at once (aborted, timer cleared, slot freed). Its reply is
  // never applied and it is not reported as a failure.
  const cancelInflight = useCallback((key) => {
    const cur = inflightRef.current[key];
    if (!cur) return;
    inflightRef.current[key] = null;
    cur.cancel();
  }, []);

  // CU-4: the three requests are applied independently, each as soon as it resolves.
  const load = useCallback(async ({ silent = false, sym } = {}) => {
    const symbol = sym || chartSymbol;
    if (!silent) setLoading(true);
    const one = (key, makeReq, apply) => {
      // CU-11: no new request for a data set while its previous one (same symbol) is still in flight.
      const tag = key === "summary" ? "summary" : symbol;
      if (inflightRef.current[key]?.tag === tag) return Promise.resolve();
      seqRef.current[key] += 1;
      const seq = seqRef.current[key];
      const done = () => { if (inflightRef.current[key]?.seq === seq) inflightRef.current[key] = null; };
      // CU-13: a request that has not answered within REQUEST_TIMEOUT_MS is aborted and fails, freeing the slot.
      const ctrl = new AbortController();
      let timer;
      let stop;
      const timeout = new Promise((_, reject) => {
        stop = reject;
        timer = setTimeout(() => { ctrl.abort(); reject(new Error("timeout")); }, REQUEST_TIMEOUT_MS);
      });
      const cancel = () => {
        clearTimeout(timer);
        ctrl.abort();
        const e = new Error("cancelled");
        e.cancelled = true;
        stop(e);
      };
      inflightRef.current[key] = { tag, seq, cancel };
      return Promise.race([makeReq(ctrl.signal), timeout])
        .finally(() => { clearTimeout(timer); done(); })
        .then((r) => {
          if (seq <= appliedRef.current[key]) return;
          if (key !== "summary" && symbol !== symbolRef.current) return;
          appliedRef.current[key] = seq;
          apply(r.data);
          const t = Date.now();
          setLastOk((o) => ({ ...o, [key]: t }));
        });
    };
    const pending = [
      one("summary", (signal) => api.get("/dashboard/summary", { signal }),
        (d) => { setSummary(d); setSummaryAt(Date.now()); }),
      one("market", (signal) => api.get("/market/ohlcv", { params: { symbol, days: 90 }, signal }),
        (d) => setCandles(d?.candles || [])),
      one("trap", (signal) => api.get("/trap/scan", { params: { symbol }, signal }), (d) => setTrapPreview(d)),
    ];
    // The refresh spinner follows the summary (P&L/exposure) request only; a slow trap scan does not hold it.
    if (!silent) pending[0].catch(() => {}).finally(() => setLoading(false));
    const results = await Promise.allSettled(pending);
    // CU-14: a skipped set (slot busy) is fulfilled; a cancelled one is not a failure either.
    const failed = ["summary", "market", "trap"]
      .filter((_, i) => results[i].status === "rejected" && !results[i].reason?.cancelled);
    if (!silent && failed.length) {
      toast.error(`Failed to load dashboard: ${failed.map((k) => SET_NAMES[k]).join(", ")}`);
    }
  }, [chartSymbol]);

  const handleSymbolChange = (sym) => {
    symbolRef.current = sym;
    // Chart and trap data of the previous symbol no longer apply; their freshness restarts for the new one.
    setCandles([]);
    setTrapPreview(null);
    setLastOk((o) => ({ ...o, market: null, trap: null }));
    const t = Date.now();
    setSince((o) => ({ ...o, market: t, trap: t }));
    setChartSymbol(sym);
    load({ sym });
  };

  useEffect(() => { load(); }, [load]);

  // CU-14: on unmount every in-flight cockpit request is aborted and its timer cleared.
  useEffect(() => () => { ["summary", "market", "trap"].forEach(cancelInflight); }, [cancelInflight]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { load({ silent: true }); }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // CU-5: one 1 s clock drives "Ns ago", the stale banners and the live snapshot age (no network call).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ageOf = (key) => Math.max(0, (now - (lastOk[key] ?? since[key])) / 1000);
  const secondsAgo = lastOk.summary ? Math.floor(Math.max(0, now - lastOk.summary) / 1000) : 0;

  // CU-15: a kill-switch PUT is pending (the switch is disabled; a second toggle is ignored).
  const [killPending, setKillPending] = useState(false);
  const killPendingRef = useRef(false);

  const toggleKill = async (val) => {
    if (!summary?.risk_limits || killPendingRef.current) return;
    killPendingRef.current = true;
    setKillPending(true);
    try {
      const r = await api.put("/risk/limits", { ...summary.risk_limits, kill_switch: val });
      // CU-14: show the stored state at once (the PUT echoes it; else the requested value), drop any summary
      // request started before the toggle (its reply would show the old state), then load a fresh summary.
      const ks = typeof r?.data?.kill_switch === "boolean" ? r.data.kill_switch : val;
      setSummary((s) => (s ? { ...s, kill_switch: ks, risk_limits: { ...(s.risk_limits || {}), kill_switch: ks } } : s));
      // At most one summary request is in flight (CU-11); cancelling it means its reply is never applied.
      cancelInflight("summary");
      toast[val ? "warning" : "success"](val ? "Kill switch ACTIVE" : "Kill switch released");
      load();
    } catch {
      // CU-15: the PUT may have been saved although its reply was lost: re-read the stored state at once.
      toast.error("Kill switch change could not be confirmed — re-reading the stored state");
      cancelInflight("summary");
      load();
    } finally {
      killPendingRef.current = false;
      setKillPending(false);
    }
  };

  const slice = (() => {
    if (!summary) return { total_pnl: 0, exposure: 0, open_positions: 0 };
    if (mode === "paper") return summary.paper || summary;
    if (mode === "live") return { ...summary.live, open_positions: summary.live?.positions?.length || 0 };
    return summary.combined || summary;
  })();
  const pnl = slice.total_pnl ?? 0;
  // Paper positions at their last real mark for want of a fresh quote (P&L an estimate; RU-PAPERFILL PF-6).
  const staleCount = mode === "live" ? 0 : (summary?.paper?.stale_count ?? summary?.stale_count ?? 0);
  // Today's paper exits booked without a fresh quote, at the last real mark (P&L an estimate; RU-PAPERFILL PF-3).
  const estimatedCount = mode === "live" ? 0 : (summary?.paper?.estimated_count ?? 0);
  const pnlTone = pnl > 0 ? "profit" : pnl < 0 ? "loss" : "neutral";
  const liveBrokerCount = summary?.live?.broker_count ?? 0;
  // CU-2/CU-3: brokers left out of the live totals, and the age of the live position snapshot used.
  const showsLive = mode === "live" || mode === "combined";
  const liveFailures = (summary?.live?.sources || []).filter((s) => s.source === "error" || s.source === "skipped");
  const snapAge = typeof summary?.live?.snapshot_age_s === "number"
    ? Math.floor(summary.live.snapshot_age_s + Math.max(0, now - (summaryAt ?? now)) / 1000)
    : null;
  const snapSources = [...new Set((summary?.live?.sources || [])
    .filter((s) => typeof s.age_s === "number")
    .map((s) => (s.source === "fresh" ? "broker" : s.source)))];
  // CU-6: paper-only mark counts.
  const simulatedCount = summary?.paper?.simulated_count ?? 0;
  const overBudgetCount = summary?.paper?.over_budget_count ?? 0;
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  // CU-7: trap tile labels.
  const chainLabel = chainSourceLabel(trapPreview);
  const spotKind = spotKindOf(trapPreview?.symbol || chartSymbol);

  const equityCurve = buildEquityCurve(todayTrades);
  const showEquity = equityCurve.length >= 2;
  const lastEquityPnl = equityCurve.at(-1)?.pnl ?? 0;

  return (
    <AppShell>
      <PageHeader
        overline="Cockpit"
        title="Trading Cockpit"
        description="Live read on your strategies, paper P&L, exposure and trap exposure across your watchlist."
        actions={
          <>
            <div className="flex items-center gap-3 panel px-3 py-2">
              <ShieldAlert className={`w-4 h-4 ${summary?.kill_switch ? "txt-loss" : "txt-muted"}`} />
              <div className="overline">Kill switch</div>
              <Switch
                data-testid="cockpit-kill-switch"
                checked={!!summary?.kill_switch}
                disabled={killPending}
                onCheckedChange={toggleKill}
              />
            </div>
            <Button
              data-testid="cockpit-new-strategy-btn"
              onClick={() => navigate("/strategies")}
              className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
            >
              <Sparkles className="w-4 h-4 mr-2" /> NEW STRATEGY
            </Button>
          </>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6" data-testid="dashboard-content">
        {/* ── Toolbar ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-px panel p-1">
            {[
              { v: "paper",    label: "PAPER" },
              { v: "live",     label: liveBrokerCount > 0 ? `LIVE · ${liveBrokerCount}` : "LIVE" },
              { v: "combined", label: "COMBINED" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`dashboard-mode-${opt.v}`}
                onClick={() => setMode(opt.v)}
                className={`px-4 py-1.5 text-[11px] font-section tracking-wider transition-colors ${
                  mode === opt.v
                    ? "bg-[var(--bg-surface-2)] text-[var(--text-primary)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-[11px] font-mono-data text-[var(--text-muted)]">
            {lastOk.summary && <span data-testid="dashboard-last-refresh">{secondsAgo}s ago</span>}
            <Button
              data-testid="dashboard-manual-refresh"
              size="sm"
              variant="ghost"
              onClick={() => load()}
              disabled={loading}
              className="rounded-none h-7 w-7 p-0 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              data-testid="dashboard-auto-refresh-toggle"
              size="sm"
              variant="ghost"
              onClick={() => setAutoRefresh((v) => !v)}
              className={`rounded-none h-7 px-2 transition-colors ${autoRefresh ? "txt-profit" : "text-[var(--text-muted)]"}`}
            >
              {autoRefresh ? <Pause className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              {autoRefresh ? "LIVE" : "PAUSED"}
            </Button>
          </div>
        </div>

        {/* ── CU-5: P&L / exposure tile group staleness ── */}
        <StaleBanner testid="dashboard-stale-summary" ageS={ageOf("summary")} />

        {/* ── CU-2 / CU-3: live broker failures and live snapshot age (LIVE and COMBINED) ── */}
        {showsLive && liveFailures.length > 0 && (
          <div
            data-testid="dashboard-live-broker-errors"
            role="alert"
            className="text-[12px] font-section txt-loss border border-red-500/40 bg-red-500/5 px-3 py-2 space-y-1"
          >
            {liveFailures.map((f) => (
              <div key={f.broker} data-testid={`dashboard-live-broker-error-${f.broker}`}>
                {String(f.broker).toUpperCase()}: live positions could not be fetched — excluded from totals
                {f.error ? ` (${f.error})` : ""}
              </div>
            ))}
          </div>
        )}
        {showsLive && snapAge != null && (
          <div data-testid="dashboard-live-snapshot-age" className="text-[11px] font-mono-data text-[var(--text-muted)]">
            live positions snapshot {snapAge}s old{snapSources.length ? ` · ${snapSources.join(" + ")}` : ""}
          </div>
        )}

        {/* ── KPI row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            testid="kpi-total-pnl"
            label={`Total P&L · ${mode}`}
            value={<Money value={pnl} testid="kpi-total-pnl-value" />}
            sub={`${slice.open_positions ?? 0} open positions`
              + (staleCount ? ` · ${staleCount} position${staleCount === 1 ? "" : "s"} at the last real mark (no fresh quote)` : "")
              + (estimatedCount ? ` · ${estimatedCount} exit${estimatedCount === 1 ? "" : "s"} booked without a fresh quote — P&L an estimate` : "")}
            tone={pnlTone}
            icon={Activity}
          />
          <StatCard
            testid="kpi-exposure"
            label="Gross exposure"
            value={<Money value={slice.gross_exposure ?? slice.exposure ?? 0} decimals={0} testid="kpi-exposure-value" />}
            sub={slice.net_exposure != null ? `net ₹${fmt(slice.net_exposure, 0)} · notional deployed` : "notional deployed"}
            icon={ShieldAlert}
          />
          <StatCard
            testid="kpi-strategies"
            label="Strategies"
            value={summary?.strategies ?? "—"}
            sub={`${summary?.backtests ?? 0} backtests run`}
            icon={Sparkles}
          />
          <StatCard
            testid="kpi-trap-score"
            label={`${chartSymbol} Trap Score`}
            value={trapPreview ? `${(trapPreview.overall_trap_score * 100).toFixed(0)}%` : "—"}
            sub={trapPreview
              ? `${spotKind} ₹${fmt(trapPreview.spot, 0)} · expiry ${trapPreview.expiry ?? "—"} · ${chainLabel} chain`
              : "loading…"}
            tone={trapPreview && trapPreview.overall_trap_score > 0.6 ? "warn" : "neutral"}
          />
        </div>

        {/* ── CU-6: paper mark counts near the P&L / exposure tiles ── */}
        {summary && (
          <div data-testid="dashboard-mark-counts" className="text-[11px] font-mono-data text-[var(--text-muted)] flex flex-wrap gap-x-4 gap-y-1">
            {mode === "live" ? (
              <span data-testid="dashboard-mark-counts-live-note">
                stale / simulated / over-budget counts apply to paper positions only; live positions are valued at the broker's LTP
              </span>
            ) : (
              <>
                <span data-testid="dashboard-simulated-count" className={simulatedCount > 0 ? "txt-warn" : ""}>
                  {plural(simulatedCount, "paper position")} marked on a simulated (non-live) option chain
                </span>
                <span data-testid="dashboard-over-budget-count" className={overBudgetCount > 0 ? "txt-warn" : ""}>
                  {plural(overBudgetCount, "paper position")} opened at the 1-lot floor over budget
                </span>
                {mode === "combined" && staleCount > 0 && (
                  <span data-testid="dashboard-stale-paper-note">
                    {plural(staleCount, "paper position")} at the last real mark (no fresh quote); live positions have no such mark
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Row 2: Trade Style + Scanner Performance ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <TradeStyleCard profile={profile} />
          <div className="lg:col-span-2">
            <ScannerStrip scanners={scanners} />
          </div>
        </div>

        {/* ── Row 3: Chart + Trap ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* Today's equity curve or 90D price chart */}
          <div className="panel lg:col-span-2 p-5">
            <StaleBanner testid="dashboard-stale-market" ageS={ageOf("market")} />
            <div className="flex items-start justify-between mb-5">
              <div>
                {showEquity ? (
                  <>
                    <div className="text-[10px] text-[var(--text-muted)] tracking-[0.14em] uppercase font-section mb-1">
                      Today · Cumulative P&L
                    </div>
                    <div className={`font-display text-[32px] leading-none ${lastEquityPnl >= 0 ? "txt-profit" : "txt-loss"}`}>
                      {lastEquityPnl >= 0 ? "+" : ""}₹{fmt(lastEquityPnl)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-[var(--text-muted)] tracking-[0.14em] uppercase font-section">
                        {chartSymbol} · 90D Close
                      </span>
                      <select
                        value={chartSymbol}
                        onChange={(e) => handleSymbolChange(e.target.value)}
                        className="text-[11px] bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-secondary)] px-2 py-0.5 font-mono-data outline-none hover:border-[var(--border-strong)] transition-colors"
                      >
                        {symbols.map((s) => (
                          <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                        ))}
                      </select>
                    </div>
                    <div className="font-display text-[32px] leading-none text-[var(--text-primary)]">
                      {candles.length > 0 ? `₹${fmt(candles.at(-1)?.close ?? 0)}` : "—"}
                    </div>
                  </>
                )}
              </div>
              <Activity className="w-4 h-4 text-[var(--text-muted)] mt-1" />
            </div>

            <div className="h-72">
              {showEquity ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={equityCurve} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 6" vertical={false} />
                    <XAxis
                      dataKey="time"
                      stroke="rgba(255,255,255,0.15)"
                      tick={{ fill: "var(--text-muted)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.15)"
                      tick={{ fill: "var(--text-muted)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--bg-surface-2)",
                        border: "1px solid var(--border-strong)",
                        borderRadius: 0,
                        fontFamily: "JetBrains Mono",
                        fontSize: 11,
                      }}
                      formatter={(v) => [`₹${fmt(v)}`, "Cum. P&L"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="pnl"
                      stroke={lastEquityPnl >= 0 ? "var(--profit)" : "var(--loss)"}
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : candles.length === 0 ? (
                <div className="h-full flex items-center justify-center text-[var(--text-muted)] text-sm font-section">
                  Loading market data…
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={candles} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 6" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="rgba(255,255,255,0.15)"
                      tick={{ fill: "var(--text-muted)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      tickFormatter={(v) => v.slice(5)}
                      interval="preserveStartEnd"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.15)"
                      tick={{ fill: "var(--text-muted)", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      domain={["dataMin - 200", "dataMax + 200"]}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--bg-surface-2)",
                        border: "1px solid var(--border-strong)",
                        borderRadius: 0,
                        fontFamily: "JetBrains Mono",
                        fontSize: 11,
                      }}
                      labelStyle={{ color: "var(--text-secondary)" }}
                      itemStyle={{ color: "var(--accent)" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="close"
                      stroke="var(--accent)"
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 3, fill: "var(--accent)" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Trap alerts panel */}
          <div className="panel p-5 flex flex-col">
            <StaleBanner testid="dashboard-stale-trap" ageS={ageOf("trap")} />
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] text-[var(--text-muted)] tracking-[0.14em] uppercase font-section">
                Trap Alerts · {chartSymbol}
                {chainLabel && (
                  <span data-testid="dashboard-trap-source" className={`ml-2 ${chainLabel === "LIVE" ? "txt-profit" : "txt-warn"}`}>
                    {chainLabel} chain
                  </span>
                )}
              </div>
              {trapPreview && (
                <span className={`text-[10px] font-mono-data font-semibold px-1.5 py-0.5 border ${
                  trapPreview.overall_trap_score > 0.6
                    ? "txt-warn border-amber-500/30 bg-amber-500/5"
                    : "txt-profit border-emerald-500/30 bg-emerald-500/5"
                }`}>
                  {(trapPreview.overall_trap_score * 100).toFixed(0)}% RISK
                </span>
              )}
            </div>
            <div className="flex-1 space-y-2 min-h-0">
              {!trapPreview && (
                <div className="text-[var(--text-muted)] text-sm font-section">Scanning options chain…</div>
              )}
              {trapPreview?.suggestions?.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-[var(--profit)] font-section">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--profit)] inline-block" />
                  No high-confidence traps detected
                </div>
              )}
              {trapPreview?.suggestions?.map((s, i) => (
                <div
                  key={`${s.side}-${s.strike}`}
                  className={`border p-3 ${s.level === "HIGH" ? "border-red-500/25 bg-red-500/5" : "border-amber-500/20 bg-amber-500/5"}`}
                  data-testid={`dashboard-trap-${i}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[9px] tracking-widest uppercase font-section font-semibold ${s.level === "HIGH" ? "txt-loss" : "txt-warn"}`}>
                      {s.level} · {s.side}
                    </span>
                    <span className="font-mono-data text-[11px] text-[var(--text-secondary)]">₹{s.strike}</span>
                  </div>
                  <div className="text-[12px] text-[var(--text-secondary)] leading-snug">{s.headline}</div>
                </div>
              ))}
            </div>
            <button
              data-testid="dashboard-trap-open"
              onClick={() => navigate("/trap")}
              className="mt-4 pt-3 border-t border-[var(--border)] flex items-center justify-between text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] font-section transition-colors"
            >
              Full Trap Detection <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Live broker positions */}
        {summary?.live?.positions?.length > 0 && (
          <div className="panel p-5" data-testid="dashboard-live-positions">
            <div className="flex items-center justify-between mb-3">
              <div className="overline txt-loss">Live broker positions · real money</div>
              <span className="text-xs font-mono-data txt-loss border border-red-500/40 px-2 py-0.5">
                {summary.live.positions.length} OPEN
              </span>
            </div>
            <div className="space-y-1 text-xs font-mono-data">
              <div className="grid grid-cols-7 gap-2 txt-muted border-b border-[var(--border)] pb-2 overline">
                <span>Broker</span><span>Symbol</span><span>Product</span>
                <span className="text-right">Qty</span><span className="text-right">Avg</span>
                <span className="text-right">LTP</span><span className="text-right">P&L</span>
              </div>
              {summary.live.positions.map((p, i) => (
                <div
                  key={`${p.broker}-${p.symbol}-${i}`}
                  className="grid grid-cols-7 gap-2 py-1.5 border-b border-[var(--border)]"
                  data-testid={`dashboard-live-pos-${i}`}
                >
                  <span className="font-section">{p.broker.toUpperCase()}</span>
                  <div className="min-w-0">
                    {p.symbol}
                    {p.expiry_info ? (
                      <div><ExpiryBadge info={p.expiry_info} testid={`dashboard-live-expiry-${i}`} /></div>
                    ) : null}
                  </div>
                  <span className="txt-muted">{p.product}</span>
                  <span className={`text-right ${p.qty > 0 ? "txt-profit" : "txt-loss"}`}>{p.qty}</span>
                  <span className="text-right">{fmt(p.avg_price)}</span>
                  <span className="text-right">{fmt(p.last_price)}</span>
                  <span className={`text-right ${p.pnl > 0 ? "txt-profit" : p.pnl < 0 ? "txt-loss" : ""}`}>
                    {fmt(p.pnl)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Autopilot activity log */}
        {autopilotActivity.length > 0 && (
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-amber-400">Autopilot — today's executions</div>
              <span className="text-xs font-mono-data border border-amber-500/40 text-amber-300 px-2 py-0.5">
                {autopilotActivity.length} ORDER{autopilotActivity.length !== 1 ? "S" : ""}
              </span>
            </div>
            <div className="space-y-1 text-xs font-mono-data">
              <div className="grid grid-cols-5 gap-2 txt-muted border-b border-[var(--border)] pb-2 overline">
                <span className="col-span-2">Strategy</span>
                <span>Symbol</span>
                <span>Side</span>
                <span className="text-right">Status</span>
              </div>
              {autopilotActivity.map((a) => (
                <div key={a.id} className="grid grid-cols-5 gap-2 py-1.5 border-b border-[var(--border)]">
                  <span className="col-span-2 flex items-center gap-1 min-w-0">
                    <StrategyNameButton id={a.strategy_id} name={a.strategy_name} testid={`autopilot-strategy-name-${a.id}`} className="truncate txt-secondary" />
                    <StrategyRefChip id={a.strategy_id} testid={`autopilot-strategy-ref-${a.id}`} />
                    {a.strategy_id && "strategy_mode" in a && (
                      <StrategyStatusChip src={a} testid={`autopilot-strategy-status-${a.id}`} />
                    )}
                  </span>
                  <span className="font-section">{a.symbol}</span>
                  <span className={a.side === "BUY" ? "txt-profit" : "txt-loss"}>{a.side}</span>
                  <span className={`text-right font-section ${a.status === "COMPLETE" ? "txt-profit" : a.status === "REJECTED" ? "txt-loss" : "text-amber-400"}`}>
                    {a.status || "PLACED"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk strip */}
        {summary?.risk_limits && (
          <div className="panel p-5">
            <div className="overline mb-3">Risk policy</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono-data text-sm">
              <div>
                <div className="overline">Max drawdown</div>
                <div className="kpi-num text-2xl mt-1">{summary.risk_limits.max_drawdown_pct}%</div>
              </div>
              <div>
                <div className="overline">Daily loss cap</div>
                <div className="kpi-num text-2xl mt-1">₹{fmt(summary.risk_limits.daily_loss_cap, 0)}</div>
              </div>
              <div>
                <div className="overline">Position limit</div>
                <div className="kpi-num text-2xl mt-1">{summary.risk_limits.position_limit}</div>
              </div>
              <div>
                <div className="overline">Mode</div>
                <div className={`kpi-num text-2xl mt-1 ${summary.kill_switch ? "txt-loss" : "txt-profit"}`}>
                  {summary.kill_switch ? "HALTED" : "ARMED"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
